import React, { useState, useContext, useEffect, useCallback } from 'react';
import { UserContext } from '../UserContext';
import axios from '../axiosInstance';
import { useDialog } from '../components/ConfirmProvider';
import {
  FaInfoCircle, FaCog, FaPlus, FaTrash, FaEdit,
  FaToggleOn, FaToggleOff
} from 'react-icons/fa';
import '../Pages.css';
import '../Pips/Pips.css';   // shared design system
import './TM.css';

// rule_type → hint about what params it expects (helps users edit JSON safely)
const RULE_TYPES = [
  { v: 'large_cash',
    label: 'Large cash transaction',
    hint: '{"threshold": 100000, "currency": "NAD", "channels": ["cash"]}' },
  { v: 'structuring',
    label: 'Structuring below threshold',
    hint: '{"threshold": 100000, "lookback_days": 7, "min_count": 3, "below_pct": 90}' },
  { v: 'velocity',
    label: 'Transaction velocity burst',
    hint: '{"lookback_hours": 24, "min_count": 10}' },
  { v: 'high_risk_geo',
    label: 'High-risk geography',
    hint: '{"countries": ["AF","IR","KP","MM","SY","YE","RU","BY"]}' },
  { v: 'round_amount',
    label: 'Suspicious round-amount',
    hint: '{"threshold": 50000, "round_to": 10000}' },
  { v: 'out_of_pattern',
    label: 'Out of customer pattern',
    hint: '{"lookback_days": 90, "multiplier": 10, "min_baseline": 1000}' },
  { v: 'edd_aggregate',
    label: 'EDD daily aggregate',
    hint: '{"threshold": 5000, "currency": "NAD"}' },
  { v: 'ctr_24h_aggregate',
    label: 'CTR 24h rolling aggregate',
    hint: '{"threshold": 99999.99, "currency": "NAD", "channels": ["cash"]}' },
  { v: 'rapid_movement',
    label: 'Rapid movement / pass-through',
    hint: '{"lookback_minutes": 60, "min_pct": 80, "min_amount": 1000}' },
  { v: 'dormant_reactivation',
    label: 'Dormant account reactivation',
    hint: '{"dormancy_days": 90, "min_amount": 1000}' },
  { v: 'geo_mismatch',
    label: 'Counterparty geo mismatch',
    hint: '{"min_amount": 0}' },
  { v: 'source_of_funds_mismatch',
    label: 'Activity vs declared income',
    hint: '{"multiplier": 5, "min_actual": 5000}' },
  { v: 'agent_float_cycling',
    label: 'Agent float cycling',
    hint: '{"lookback_hours": 24, "min_volume": 100000, "max_net_ratio": 0.10}' },
  { v: 'geo_consistency',
    label: 'Origin vs declared residence',
    hint: '{"min_amount": 0}' },
  { v: 'impossible_travel',
    label: 'Impossible travel between txns',
    hint: '{"max_kmh": 800, "lookback_hours": 12, "min_distance_km": 200}' },
  { v: 'cross_channel_geo_inconsistency',
    label: 'Cross-channel geo inconsistency',
    hint: '{"lookback_minutes": 30, "min_distance_km": 100}' },
  { v: 'ai_anomaly',
    label: 'AI unsupervised anomaly (z-score)',
    hint: '{"min_z_score": 3, "min_baseline_count": 10, "min_amount": 1000}' },
  { v: 'ai_supervised',
    label: 'AI supervised classifier (feedback-trained)',
    hint: '{"min_prob": 0.7, "min_amount": 1000}' }
];
const SEVERITIES = ['low','medium','high','critical'];

const hasPermission = (user, name) =>
  !!user && (
    user.is_system_admin === true ||
    (Array.isArray(user.permissions) && user.permissions.includes(name))
  );

const blankRule = () => ({
  code: '',
  name: '',
  description: '',
  rule_type: 'large_cash',
  severity: 'medium',
  params: '{}',
  is_active: true
});

function ManageRules() {
  const { user } = useContext(UserContext);
  const { confirm } = useDialog();
  const canRead   = hasPermission(user, 'tm_rules.read');
  const canCreate = hasPermission(user, 'tm_rules.create');
  const canUpdate = hasPermission(user, 'tm_rules.update');
  const canToggle = hasPermission(user, 'tm_rules.toggle');
  const canDelete = hasPermission(user, 'tm_rules.delete');

  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editor, setEditor] = useState(null);   // { mode: 'create'|'edit', data, paramsText, existingId? }
  const [busy, setBusy] = useState(false);

  const fetchRules = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await axios.get('/tm-rules');
      setRules(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setError(e.message || 'Failed to load rules');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (canRead) fetchRules(); }, [canRead, fetchRules]);

  const openCreate = () => {
    const fresh = blankRule();
    const hint = RULE_TYPES.find(t => t.v === fresh.rule_type)?.hint || '{}';
    setEditor({ mode: 'create', data: fresh, paramsText: hint });
  };

  const openEdit = (rule) => {
    setEditor({
      mode: 'edit',
      existingId: rule.id,
      data: {
        code: rule.code,                              // immutable in edit mode
        name: rule.name,
        description: rule.description || '',
        rule_type: rule.rule_type,                    // immutable in edit mode
        severity: rule.severity,
        is_active: rule.is_active
      },
      paramsText: JSON.stringify(rule.params || {}, null, 2)
    });
  };

  const submit = async () => {
    if (!editor) return;
    setBusy(true); setError(null);
    try {
      let parsedParams;
      try {
        parsedParams = JSON.parse(editor.paramsText || '{}');
      } catch (e) {
        throw new Error('Params is not valid JSON: ' + e.message);
      }

      if (editor.mode === 'create') {
        await axios.post('/tm-rules', {
          code: editor.data.code.trim().toUpperCase(),
          name: editor.data.name.trim(),
          description: editor.data.description.trim() || undefined,
          rule_type: editor.data.rule_type,
          severity: editor.data.severity,
          params: parsedParams,
          is_active: editor.data.is_active
        });
      } else {
        await axios.patch(`/tm-rules/${editor.existingId}`, {
          name: editor.data.name.trim(),
          description: editor.data.description.trim() || undefined,
          severity: editor.data.severity,
          params: parsedParams,
          is_active: editor.data.is_active
        });
      }
      setEditor(null);
      fetchRules();
    } catch (e) {
      setError(e.response?.data?.error?.message || e.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const toggleRule = async (id) => {
    try {
      await axios.post(`/tm-rules/${id}/toggle`);
      fetchRules();
    } catch (e) {
      setError(e.response?.data?.error?.message || e.message || 'Toggle failed');
    }
  };

  const deleteRule = async (rule) => {
    const ok = await confirm({
      title: 'Delete TM rule',
      message: `Delete rule ${rule.code}? This cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true
    });
    if (!ok) return;
    try {
      await axios.delete(`/tm-rules/${rule.id}`);
      fetchRules();
    } catch (e) {
      setError(e.response?.data?.error?.message || e.message || 'Delete failed');
    }
  };

  if (user && !canRead) {
    return (
      <div className="page-container">
        <div className="alert-banner alert-banner--warning" style={{ marginTop: '2rem' }}>
          <span className="alert-banner__body">
            <FaInfoCircle /> You don't have permission to view TM rules.
            Ask your administrator for the <code>tm_rules.read</code> permission.
          </span>
        </div>
      </div>
    );
  }

  const globalRules = rules.filter(r => !r.organisation_id);
  const orgRules    = rules.filter(r =>  r.organisation_id);

  return (
    <div className="page-container">
      {/* Stats */}
      <div className="stats-section">
          {/* Shared stat-card layout (Pips.css): compact cards */}
          <div className="stats-layout__rest">
        <div className="stats-grid">
          <div className="stat-card stat-card--teal">
            <div className="stat-label">Total rules</div>
            <div className="stat-value stat-value--teal">{rules.length}</div>
          </div>
          <div className="stat-card stat-card--blue">
            <div className="stat-label">Active</div>
            <div className="stat-value stat-value--blue">{rules.filter(r => r.is_active).length}</div>
          </div>
          <div className="stat-card stat-card--orange">
            <div className="stat-label">Disabled</div>
            <div className="stat-value stat-value--orange">{rules.filter(r => !r.is_active).length}</div>
          </div>
          <div className="stat-card stat-card--brown">
            <div className="stat-label">Global defaults</div>
            <div className="stat-value">{globalRules.length}</div>
          </div>
        </div>
      </div>
        </div>

      {/* Header + create button */}
      <section className="form-section" style={{ marginBottom: '1rem' }}>
        <header className="form-section__head">
          <h4 className="form-section__title">TM rules</h4>
          <p className="form-section__subtitle">
            Configurable rules that scan transactions and emit alerts. Global rules apply to every organisation; org-scoped rules add to (or override) defaults.
          </p>
        </header>
        {canCreate && (
          <button className="export-button" onClick={openCreate}>
            <FaPlus /> New rule
          </button>
        )}
      </section>

      {error && (
        <div className="alert-banner alert-banner--danger" style={{ marginBottom: '1rem' }}>
          <span className="alert-banner__body"><FaInfoCircle /> {error}</span>
        </div>
      )}
      {loading && <p>Loading…</p>}

      {/* Rule list */}
      {!loading && rules.length === 0 && (
        <p style={{ color: 'var(--text-secondary,#607d8b)' }}>No rules configured.</p>
      )}

      {[['Global rules', globalRules], ['Organisation-specific rules', orgRules]].map(([title, list]) => (
        list.length > 0 && (
          <div key={title} style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.06em',
                         color: 'var(--text-secondary)', margin: '0 0 0.6rem' }}>{title}</h3>
            {list.map(rule => (
              <div key={rule.id}
                   style={{
                     background: 'var(--card-bg,#fff)',
                     border: '1px solid var(--card-border,#e3e6ea)',
                     borderLeft: `4px solid ${rule.is_active ? 'var(--accent,#00796b)' : 'var(--text-muted,#90a4ae)'}`,
                     padding: '0.9rem 1.1rem',
                     marginBottom: '0.5rem',
                     opacity: rule.is_active ? 1 : 0.7
                   }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.7rem', letterSpacing: '0.06em', textTransform: 'uppercase',
                                  color: 'var(--text-secondary)', fontWeight: 700 }}>
                      {rule.code} · {rule.rule_type}
                    </div>
                    <h4 style={{ margin: '0.2rem 0 0.3rem' }}>{rule.name}</h4>
                    {rule.description && <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{rule.description}</p>}
                    <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                      <span className={`tm-pill tm-pill--sev-${rule.severity}`}>{rule.severity}</span>
                      <span className="tm-pill">{rule.alert_count || 0} alerts fired</span>
                      {rule.organisation_name && <span className="tm-pill">{rule.organisation_name}</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.35rem', flexShrink: 0 }}>
                    {canToggle && (
                      <button className="action-button" onClick={() => toggleRule(rule.id)}
                              title={rule.is_active ? 'Disable' : 'Enable'}>
                        {rule.is_active ? <FaToggleOn className="toggle-icon--on" /> : <FaToggleOff className="toggle-icon--off" />}
                      </button>
                    )}
                    {canUpdate && (
                      <button className="action-button" onClick={() => openEdit(rule)}
                              title="Edit">
                        <FaEdit />
                      </button>
                    )}
                    {canDelete && (
                      <button className="action-button delete" onClick={() => deleteRule(rule)}
                              title="Delete">
                        <FaTrash />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      ))}

      {/* Create / edit modal */}
      {editor && (
        <div className="modal-overlay" onClick={() => !busy && setEditor(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 680 }}>
            <button className="modal-close" onClick={() => !busy && setEditor(null)}>×</button>
            <h2 style={{ marginTop: 0 }}>
              <FaCog style={{ marginRight: '0.4rem' }} />
              {editor.mode === 'create' ? 'New TM rule' : `Edit rule ${editor.data.code}`}
            </h2>

            <div className="form-grid-2">
              <div className="form-group">
                <label>Code (UPPER_SNAKE)</label>
                <input type="text" value={editor.data.code} disabled={editor.mode === 'edit'}
                       onChange={e => setEditor(ed => ({ ...ed, data: { ...ed.data, code: e.target.value.toUpperCase() } }))} />
              </div>
              <div className="form-group">
                <label>Rule type</label>
                <select value={editor.data.rule_type} disabled={editor.mode === 'edit'}
                        onChange={e => {
                          const t = e.target.value;
                          const hint = RULE_TYPES.find(x => x.v === t)?.hint || '{}';
                          setEditor(ed => ({ ...ed, data: { ...ed.data, rule_type: t }, paramsText: hint }));
                        }}>
                  {RULE_TYPES.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
                </select>
              </div>
              <div className="form-group form-group--full">
                <label>Name</label>
                <input type="text" value={editor.data.name}
                       onChange={e => setEditor(ed => ({ ...ed, data: { ...ed.data, name: e.target.value } }))} />
              </div>
              <div className="form-group form-group--full">
                <label>Description</label>
                <textarea rows={2} value={editor.data.description}
                          onChange={e => setEditor(ed => ({ ...ed, data: { ...ed.data, description: e.target.value } }))} />
              </div>
              <div className="form-group">
                <label>Severity</label>
                <select value={editor.data.severity}
                        onChange={e => setEditor(ed => ({ ...ed, data: { ...ed.data, severity: e.target.value } }))}>
                  {SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>&nbsp;</label>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                               fontWeight: 600, textTransform: 'none' }}>
                  <input type="checkbox" checked={editor.data.is_active}
                         onChange={e => setEditor(ed => ({ ...ed, data: { ...ed.data, is_active: e.target.checked } }))} />
                  Active
                </label>
              </div>
              <div className="form-group form-group--full">
                <label>Params (JSON)</label>
                <textarea rows={6} style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                          value={editor.paramsText}
                          onChange={e => setEditor(ed => ({ ...ed, paramsText: e.target.value }))} />
                <p style={{ margin: '0.3rem 0 0', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                  Example for <strong>{editor.data.rule_type}</strong>:{' '}
                  <code>{RULE_TYPES.find(t => t.v === editor.data.rule_type)?.hint}</code>
                </p>
              </div>
            </div>

            <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="btn-cancel" disabled={busy} onClick={() => setEditor(null)}>Cancel</button>
              <button className="export-button" disabled={busy} onClick={submit}>
                {busy ? 'Saving…' : editor.mode === 'create' ? 'Create rule' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageRules;
